import { Box, Flex } from '@mantine/core';
import type { ExchangeJwtResponse } from '@repo/api-codegen';
import { useExchangeJwt } from '@repo/api-codegen';
import { OvalLoader } from '@repo/common/components/Loader';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { consumeAuthCallbackJwt } from '../../utils/authCallbackToken';
import { processAuthEvent } from '../../utils/crossTabAuth';
import { EmptyState } from '../EmptyState';

// Consume this at module initialization so React StrictMode cannot read and delete
// the one-time token twice. index.html has already removed it from the visible URL.
const jwt = consumeAuthCallbackJwt();

export function AuthCallback() {
	const [error, setError] = useState<Error | null>(() =>
		jwt ? null : new Error('Authentication token is missing or has expired')
	);
	const { provider } = useParams();

	const handleAuthenticationResponse = useCallback(
		(authResponse: ExchangeJwtResponse) => {
			if (authResponse.id) {
				// This is important as it will clear the query cache and reset the user state, in case a user was previously logged in. It will also sign out other tabs
				processAuthEvent({
					type: 'SIGN_IN',
					redirectPath: '/',
				});
			}
			return false;
		},
		[]
	);

	const { mutate: exchangeJwt, isPending } = useExchangeJwt({
		onSuccess: (response) => {
			// The response contains user identity data (id, email, workspace_id, etc.)
			// and cookies are set automatically by the backend
			// Note: The generated types incorrectly show response as undefined, but the actual
			// backend returns identity data via token_with_identity_response
			if (response && typeof response === 'object' && 'id' in response) {
				handleAuthenticationResponse(response);
			} else if (
				response &&
				typeof response === 'object' &&
				'error' in response
			) {
				setError(new Error(response.error));
			}
		},
		onError: (validationError) => {
			setError(new Error(validationError?.detail || 'JWT validation failed'));
		},
	});

	// Handle JWT authentication when component mounts
	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		if (jwt && provider) {
			exchangeJwt({
				body: {
					jwt,
				},
				pathParams: {
					provider,
				},
			});
		}
	}, [provider, exchangeJwt]);

	const isCallbackInProgress = !!jwt && isPending;

	return (
		<Box sx={{ height: '100vh', width: '100%' }}>
			{error ? (
				<EmptyState
					title="Authentication Error"
					description={error.message}
					iconName="alertCircle"
					size="lg"
					includeGoBack
				/>
			) : (
				<Flex align="center" justify="center" style={{ height: '100%' }}>
					{isCallbackInProgress && <OvalLoader size="lg" />}
				</Flex>
			)}
		</Box>
	);
}

export default AuthCallback;
